import { Component, HostBinding, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { EMPTY_ORGANIZATION } from '../../../models/organization';
import { CountriesService } from '../../../services/countries.services';
import { UserService } from '../../../../shared/services/user.service';
import { OrganizationDetails } from '../../../models/organization-details';
import { UserInfo } from '../../../../shared/models/user-info';
import { User } from '../../../../shared/models/user';
import { OrganizationBasics } from '../../../models/v2/organization-info';
import { Country } from '../../../models/v2/country';
import { OrganizationService } from '../../../services/organization.service';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { FlowChartComponent } from '../org-flow-chart/org-flow-chart.component';

import {
	OrganizationCardDetailsIdentityForm,
	OrganizationCardDetailsIdentityComponent
} from './organization-card-details-identity.component';

@Component({
	selector: 'copo-organization-card-details',
	templateUrl: './organization-card-details.component.html',
	styleUrl: './organization-card-details.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,

		MatButtonModule,
		RouterLink,
		TranslateModule,
		MatIconModule,
		OrganizationCardDetailsIdentityComponent,
		FlowChartComponent,
		LoadingSpinnerComponent
	]
})
export class OrganizationCardDetailsComponent implements OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	organization?: OrganizationDetails;
	userInfo?: UserInfo;
	suppliers: string[] = [];
	administratorEmail?: string;
	countries?: Country[];
	organizationId = EMPTY_ORGANIZATION.id;
	orgIdForflowChart = EMPTY_ORGANIZATION.id;
	fieldsForUser = [
		'firstName',
		'lastName',
		'language',
		'address.email'
	];

	isLoading = true;
	editForm = false;
	manageOrganization? = false;
	enableSave? = false;
	orgClone?: OrganizationDetails;
	hasAddSubOrgService = false;
	user: User;
	private routeSub: Subscription = Subscription.EMPTY;
	private organizationSub: Subscription = Subscription.EMPTY;
	private userServiceSub: Subscription = Subscription.EMPTY;

	constructor(
		private readonly organizationService: OrganizationService,
		private readonly countriesService: CountriesService,
		private readonly route: ActivatedRoute,
		private readonly userService: UserService
	) {
		this.user = this.userService.user;
		this.hasAddSubOrgService = this.user.hasServiceParameterEqualsTo('PORUM', 'AddSubOrg', 'True');
	}

	ngOnInit() {
		this.organizationId = EMPTY_ORGANIZATION.id;
		if (this.route.parent === null) {
			return;
		}
		this.routeSub = this.route.parent.paramMap.subscribe(params => {
			const idString = params.get('id');
			if (idString === null) {
				return;
			}
			this.organizationId = Number(idString);
			if (this.organization) {
				this.organization.id = this.organizationId;
			}

			this.orgIdForflowChart = this.organizationId;
			const loginUser = this.userService.loggedUser;
			if (loginUser === undefined) {
				return;
			}
			this.manageOrganization = loginUser.canUpdateOrganization(this.organizationId);
			this.setOrganization(idString);
		});
	}

	ngOnDestroy() {
		this.userServiceSub.unsubscribe();
		this.routeSub.unsubscribe();
		this.organizationSub.unsubscribe();
	}

	getAdministratorParam() {
		if (this.organization?.administrator.id === this.userService.user.id) {
			return 'self';
		}
		return this.organization?.administrator.id;
	}

	async onEdit() {
		this.editForm = true;
		if (this.organization) {
			this.orgClone = {
				...this.organization,
				address: {
					...this.organization.address
				}
			};
		}
		try {
			if (this.countries) {
				return;
			}
			await this.countriesService.getCountries().then(countries => {
				countries.sort((a, b) => a.countryDescription.localeCompare(b.countryDescription));
				this.countries = countries;
			});
		} catch (err) {
			console.error('Error from http get current country detail', err);
		}
	}

	onCancel() {
		this.editForm = false;
		this.organization = this.orgClone;
	}

	onOrganizationCardDetailsIdentityChange(form: OrganizationCardDetailsIdentityForm) {
		if (!this.organization) {
			return;
		}
		this.organization.name = form.name;
		this.organization.vatNumber = form.vatNumber;
		this.organization.address = {
			address: form.address,
			zipCode: form.postCode,
			city: form.city,
			county: form.stateOrCounty,
			countryCode: form.country.countryCode,
			countryCodeDescription: form.country.countryDescription
		};
	}

	async onSave() {
		if (this.organization?.id === undefined) {
			return;
		}
		const organizationBasics: OrganizationBasics = {
			name: this.orgClone && this.organization.name !== this.orgClone.name ? this.organization.name : null,
			vatNumber: this.orgClone && this.organization.vatNumber !== this.orgClone.vatNumber
				? this.organization.vatNumber : null,
			address: this.orgClone && this.organization.address.address !== this.orgClone.address.address
				? this.organization.address.address : null,
			postCode: this.orgClone && this.organization.address.zipCode !== this.orgClone.address.zipCode
				? this.organization.address.zipCode : null,
			city: this.orgClone && this.organization.address.city !== this.orgClone.address.city
				? this.organization.address.city : null,
			stateOrCounty: this.orgClone && this.organization.address.county !== this.orgClone.address.county
				? this.organization.address.county : null,
			country: this.orgClone && this.organization.address.countryCode
			!== this.orgClone.address.countryCode ? this.organization.address.countryCode : null,
		};
		try {
			await this.organizationService.organizationInfoToUpdate(
				this.organization.id,
				organizationBasics
			).then(() => {
				if (organizationBasics.name) {
					this.organizationService.setOrgName(organizationBasics.name);
				}
			});
		} catch (err) {
			console.error('Error from http put current org detail', err);
			this.organization = this.orgClone;
		}

		this.editForm = false;
		this.orgClone = undefined;
	}

	doCheck(formValidation: boolean): void {
		this.enableSave = formValidation;
	}

	private getSuppliers() {
		if (this.organization?.suppliers) {
			for (const supplier of this.organization.suppliers) {
				if (!this.suppliers.includes(supplier.name)) {
					this.suppliers.push(supplier.name);
				}
			}
		}
		return this.suppliers;
	}

	private setOrganization(
		organizationId: string
	) {
		this.organizationSub = this.organizationService.findOrganizationDetails(organizationId)
			.subscribe({
				next: organization => {
					this.organization = organization;
					this.getSuppliers();
					this.setAdministratorEmail();
					this.isLoading = false;
				},
				error: (err: unknown) => {
					console.error('Error while fetching organization details', err);
					this.isLoading = false;
				}
			});
	}

	private setAdministratorEmail() {
		this.userServiceSub = this.userService.getUserDetailsById(
			String(this.organization?.administrator.id),
			this.fieldsForUser
		).subscribe({
			next: userInfo => {
				this.userInfo = userInfo;
				this.administratorEmail = this.userInfo.address.email;
			}
		});
	}
}
